import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CaretUpDown, Check, User, X } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * Combobox de clientes existentes. Permite seleccionar uno cargado o dejar en blanco
 * para que se cree uno nuevo automáticamente al guardar (via upsert_cliente_from_pedido).
 *
 * Props:
 *   value: { id, nombre } | null — cliente actualmente seleccionado
 *   onSelect(cliente | null)     — cliente completo elegido o null para limpiar
 *   placeholder, testId
 */
export default function ClienteCombobox({ value, onSelect, placeholder = "Buscar cliente ya cargado…", testId = "cliente-combobox" }) {
  const [open, setOpen] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/clientes");
        setClientes(Array.isArray(data) ? data : []);
      } catch { setClientes([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clientes.slice(0, 20);
    return clientes.filter((c) => {
      const hay = [c.nombre, c.cuit, c.email, c.telefono].map((x) => (x || "").toString().toLowerCase()).join(" ");
      return hay.includes(q);
    }).slice(0, 20);
  }, [clientes, query]);

  const selectedLabel = value?.nombre
    ? value.nombre + (value.cuit ? ` · ${value.cuit}` : "")
    : "";

  return (
    <div className="flex gap-1" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between flex-1 font-normal h-9"
            data-testid={`${testId}-trigger`}
          >
            <span className="flex items-center gap-2 truncate">
              <User size={14} weight="duotone" className="text-neutral-500" />
              <span className={selectedLabel ? "" : "text-neutral-400"}>
                {selectedLabel || placeholder}
              </span>
            </span>
            <CaretUpDown size={14} className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[92vw] sm:w-[420px] sm:max-w-[420px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Buscar por nombre, CUIT, email o teléfono…"
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId}-input`}
            />
            <CommandList>
              <CommandEmpty>
                {loading ? "Cargando clientes…" : (
                  <div className="py-3 px-2 text-xs text-neutral-500">
                    Sin coincidencias. Dejalo en blanco y al guardar se crea un cliente nuevo.
                  </div>
                )}
              </CommandEmpty>
              {filtered.length > 0 && (
                <CommandGroup heading={`${filtered.length} cliente${filtered.length === 1 ? "" : "s"}`}>
                  {filtered.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={c.id}
                      onSelect={() => { onSelect && onSelect(c); setOpen(false); setQuery(""); }}
                      data-testid={`${testId}-option-${c.id}`}
                      className="cursor-pointer"
                    >
                      <Check className={cn("mr-2 h-4 w-4", value?.id === c.id ? "opacity-100" : "opacity-0")} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{c.nombre}</div>
                        <div className="text-[11px] text-neutral-500 truncate">
                          {[c.cuit && `CUIT ${c.cuit}`, c.email, c.telefono, c.localidad].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value?.id && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onSelect && onSelect(null)}
          title="Limpiar selección"
          data-testid={`${testId}-clear`}
          className="h-9"
        >
          <X size={14} />
        </Button>
      )}
    </div>
  );
}
